import * as Clipboard from 'expo-clipboard';
import { Copy } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { IntakeCredentialValue } from '../utils';

interface LeadSourceSecretModalProps {
  visible: boolean;
  credential: IntakeCredentialValue | null;
  onDismiss: () => void;
}

// The plaintext appears exactly once, here — the server keeps only a hash
// (design §4.10), so there is deliberately no re-reveal path. Dismissal
// requires an explicit acknowledgement; the backdrop and the hardware back
// button are both routed through the same guard.
export function LeadSourceSecretModal({
  visible,
  credential,
  onDismiss,
}: Readonly<LeadSourceSecretModalProps>) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const handleCopy = async () => {
    if (!credential) return;
    await Clipboard.setStringAsync(credential.value);
    setAnnouncement(t('leadSources.secretModal.copied'));
  };

  const handleGuardedDismiss = () => {
    if (acknowledged) onDismiss();
  };

  const handleDone = () => {
    setAcknowledged(false);
    setAnnouncement('');
    onDismiss();
  };

  const valueLabel =
    credential?.kind === 'url'
      ? t('leadSources.secretModal.urlLabel')
      : t('leadSources.secretModal.keyLabel');

  return (
    <Sheet visible={visible} onClose={handleGuardedDismiss} position="center" size="sm" hideClose>
      <VStack spacing="lg" accessibilityViewIsModal>
        <Text variant="h3" color={colors.text.primary} accessibilityRole="header">
          {t('leadSources.secretModal.title')}
        </Text>
        <Text variant="body" color={colors.status.warning.main}>
          {t('leadSources.secretModal.warning')}
        </Text>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.tertiary}>
            {valueLabel}
          </Text>
          <HStack spacing="sm" align="center" style={styles.valueRow}>
            <Text
              variant="body"
              color={colors.text.primary}
              style={styles.valueText}
              selectable
              numberOfLines={3}
            >
              {credential?.value ?? ''}
            </Text>
            <Pressable
              onPress={handleCopy}
              accessibilityRole="button"
              accessibilityLabel={t('leadSources.actions.copy')}
              style={styles.copyButton}
              hitSlop={8}
            >
              <Copy size={18} color={colors.primary[500]} />
            </Pressable>
          </HStack>
        </VStack>

        <Text accessibilityLiveRegion="polite" variant="caption" color={colors.status.success.main}>
          {announcement}
        </Text>

        <Pressable
          onPress={() => setAcknowledged((prev) => !prev)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: acknowledged }}
          accessibilityLabel={t('leadSources.secretModal.acknowledge')}
          style={styles.acknowledgeRow}
        >
          <View style={[styles.checkbox, acknowledged && styles.checkboxChecked]} />
          <Text variant="body" color={colors.text.primary} style={styles.acknowledgeLabel}>
            {t('leadSources.secretModal.acknowledge')}
          </Text>
        </Pressable>

        <Button
          variant="primary"
          onPress={handleDone}
          disabled={!acknowledged}
          accessibilityLabel={t('leadSources.actions.done')}
          accessibilityState={{ disabled: !acknowledged }}
          accessibilityHint={
            acknowledged ? undefined : t('leadSources.secretModal.doneDisabledHint')
          }
        >
          <Text variant="body" color={colors.background.primary} style={styles.doneLabel}>
            {t('leadSources.actions.done')}
          </Text>
        </Button>
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  valueRow: {
    padding: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.card,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  valueText: {
    flex: 1,
    minWidth: 0,
    fontFamily: 'monospace',
  },
  copyButton: {
    padding: tokens.spacing.xs,
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  acknowledgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    minHeight: 44,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: tokens.radius.sm,
    borderWidth: 2,
    borderColor: colors.border.subtle,
  },
  checkboxChecked: {
    backgroundColor: colors.primary[500],
    borderColor: colors.primary[500],
  },
  acknowledgeLabel: {
    flex: 1,
  },
  doneLabel: {
    fontWeight: '600',
  },
});
